import { z } from "zod";
import type { DuplicateAdIdentity } from "./ads-manager-duplication";

const minor = z.number().int().positive().max(Number.MAX_SAFE_INTEGER);
export const duplicationLaunchSchema = z.object({
  accountId: z.string().regex(/^(act_)?\d+$/i).transform(v => v.replace(/^act_/i, "")),
  adIds: z.array(z.string().regex(/^\d+$/)).min(1).max(25).refine(v => new Set(v).size === v.length),
  campaign: z.object({
    name: z.string().trim().min(1).max(255),
    budgetMode: z.enum(["abo", "cbo"]),
    cboDailyBudgetMinor: minor.optional(),
  }).strict(),
  structure: z.enum(["one_adset_per_ad", "single_adset"]).default("one_adset_per_ad"),
  adsetDailyBudgetMinor: minor.optional(),
  adNameSuffix: z.string().max(100).default(" - Copy"),
  live: z.boolean().default(false),
  requestKey: z.string().regex(/^[A-Za-z0-9_-]{8,128}$/),
}).strict().superRefine((v, ctx) => {
  if (v.campaign.budgetMode === "abo" && (!v.adsetDailyBudgetMinor || v.campaign.cboDailyBudgetMinor !== undefined)
    || v.campaign.budgetMode === "cbo" && (!v.campaign.cboDailyBudgetMinor || v.adsetDailyBudgetMinor !== undefined))
    ctx.addIssue({ code: "custom", message: "ABO requires only an ad set daily budget; CBO requires only a campaign daily budget." });
});
export type DuplicationLaunch = z.infer<typeof duplicationLaunchSchema>;
export type DuplicationStepState = "planned" | "submitting" | "received" | "success" | "rejected" | "needs_review";
export interface DuplicationOutcome {
  attempt: number;
  rawResponse: string | null;
  httpStatus: number | null;
  ok: boolean;
  error: string | null;
  at: string;
}
export interface DuplicationStep {
  stepKey: string;
  action: "create_campaign" | "duplicate_adset" | "set_adset_budget" | "create_ad";
  ordinal: number;
  adIndex?: number;
  adsetIndex?: number;
}
export interface DuplicationPlan {
  version: 1;
  input: DuplicationLaunch;
  observedAt: string;
  objective: string;
  specialAdCategories: string[];
  ads: DuplicateAdIdentity[];
  adsets: Array<{ sourceAdsetId: string; sourceAdIndex: number }>;
  steps: DuplicationStep[];
  disclosures: string[];
}
export interface DuplicationResult {
  dryRun: boolean;
  campaignId: string | null;
  adsetIds: string[];
  ads: Array<{ sourceAdId: string; adId: string | null; creativeId: string | null; objectStoryId: string | null }>;
  completedSteps: number;
  totalSteps: number;
  safeToResume: boolean;
  partialSuccess: boolean;
  disclosures: string[];
}
export const DUPLICATION_REVIEW_MESSAGE = "Do not relaunch, check Ads Manager. Meta may have received this write; safe continuation cannot be proven.";
